// @flow

import {useEffect, useState} from "react";

const POLL_INTERVAL_MS = 500;

/**
 * Detects whether the Google Maps script failed to load (e.g. because a
 * cookie-consent tool such as CCM19 rewrote the script tag to
 * type="text/x-blocked-script"). In that case `withScriptjs` would show its
 * loading element forever without any console error.
 *
 * Returns `true` once `window.google.maps` has not appeared within
 * `timeoutMs` after mount. Keeps polling afterwards, so the flag flips back
 * to `false` automatically when the user grants consent later and the
 * consent tool activates the blocked script without a page reload.
 */
export const useGoogleMapsBlocked = (timeoutMs: number = 8000): boolean => {
	const [blocked, setBlocked] = useState(false);

	useEffect(() => {
		const startedAt = Date.now();
		let interval = null;

		const check = () => {
			if (window.google && window.google.maps) {
				setBlocked(false);
				if (interval) {
					clearInterval(interval);
					interval = null;
				}
				return;
			}
			setBlocked(Date.now() - startedAt >= timeoutMs);
		};

		check();
		interval = setInterval(check, POLL_INTERVAL_MS);
		return () => {
			if (interval) {
				clearInterval(interval);
			}
		};
	}, [timeoutMs]);

	return blocked;
};
